import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { BasicService } from 'src/service/basic.service';
import { DocumentMappingService } from 'src/service/document-mapping.service';
import { Cr02DialogService } from 'src/app/shared/cr02-dialog/cr02-dialog.service';

type StatusKey = 'processing' | 'failed' | 'draft' | 'published' | 'not_started';

@Component({
  selector: 'app-documentmapping',
  templateUrl: './documentmapping.component.html',
  styleUrls: ['./documentmapping.component.scss']
})
export class DocumentmappingComponent implements OnInit, OnDestroy {

  suppliers: any[] = [];
  totalrec = 0;
  index = 1;
  num_of_pages = 0;
  itemsPerPage = 10;
  rowsPerOptions = [10, 25, 50, 100];
  currentPage = 1;
  search_value = '';
  searchDraft = '';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  gotoInput = '';
  gotoError = false;

  uploadOpen = false;
  uploadSupplierId = '';
  uploadSupplierName = '';
  uploadSupplierLocked = false;
  uploadFile: File | null = null;
  uploadDragging = false;
  uploading = false;
  uploadProgress = 0;
  uploadSuccess = false;
  uploadSuccessMessage = '';
  uploadError = '';
  private uploadTimer: ReturnType<typeof setInterval> | null = null;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private readonly POLL_INTERVAL = 3000;

  openHistory = new Set<string>();
  attemptsMap: Record<string, any[]> = {};
  loadingHistory = new Set<string>();

  activeTab = 'all';
  statusCounts: Record<string, number> = {
    all: 0, published: 0, draft: 0, failed: 0, processing: 0, not_started: 0
  };
  tabs: Array<{ key: string; label: string; status: string }> = [
    { key: 'all', label: 'All Suppliers', status: '' },
    { key: 'published', label: 'Published', status: 'published' },
    { key: 'draft', label: 'Draft', status: 'draft' },
    { key: 'failed', label: 'Failed', status: 'failed' },
    { key: 'processing', label: 'Processing', status: 'processing' },
    { key: 'not_started', label: 'Not started', status: 'not_started' }
  ];

  @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>;

  constructor(
    public basicService: BasicService,
    private documentMappingService: DocumentMappingService,
    private router: Router,
    private dialog: Cr02DialogService
  ) { }

  ngOnInit(): void {
    this.loadDashboard(0);
  }

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    if (this.uploadTimer) {
      clearInterval(this.uploadTimer);
    }
  }

  get rangeLabel(): string {
    if (!this.totalrec) {
      return 'No suppliers found';
    }
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(start + this.suppliers.length - 1, this.totalrec);
    return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${this.totalrec.toLocaleString()} suppliers`;
  }

  get gotoPlaceholder(): string {
    return `1–${this.num_of_pages || 1}`;
  }

  get pagerItems(): Array<number | '…'> {
    const current = this.currentPage;
    const total = Math.max(1, this.num_of_pages || 1);
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const set = new Set<number>([1, total, current - 1, current, current + 1]);
    if (current <= 3) {
      [2, 3, 4].forEach((p) => set.add(p));
    }
    if (current >= total - 2) {
      [total - 3, total - 2, total - 1].forEach((p) => set.add(p));
    }
    const pages = [...set].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const out: Array<number | '…'> = [];
    pages.forEach((p, i) => {
      if (i > 0 && p - pages[i - 1] > 1) {
        out.push('…');
      }
      out.push(p);
    });
    return out;
  }

  tabCount(key: string): number {
    return Number(this.statusCounts[key] || 0);
  }

  setTab(key: string): void {
    if (this.activeTab === key) {
      return;
    }
    this.activeTab = key;
    this.gotoInput = '';
    this.gotoError = false;
    this.openHistory.clear();
    this.loadDashboard(0);
  }

  currentStatusFilter(): string {
    const tab = this.tabs.find((t) => t.key === this.activeTab);
    return tab ? tab.status : '';
  }

  calculatePages(): void {
    this.num_of_pages = Math.ceil(this.totalrec / this.itemsPerPage);
  }

  onSearchChange(value: string): void {
    this.searchDraft = value || '';
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => {
      const trimmed = (this.searchDraft || '').trim();
      if (trimmed.length >= 2 || trimmed.length === 0) {
        this.search_value = trimmed.length >= 2 ? trimmed : '';
        this.gotoInput = '';
        this.gotoError = false;
        this.openHistory.clear();
        this.loadDashboard(0);
      }
    }, 300);
  }

  onRowsPerChange(value: string | number): void {
    const next = Number(value);
    if (!this.rowsPerOptions.includes(next)) {
      return;
    }
    this.itemsPerPage = next;
    this.gotoInput = '';
    this.gotoError = false;
    this.loadDashboard(0);
  }

  goToPage(page: number | string): void {
    const pageNum = Math.floor(Number(page));
    if (!Number.isFinite(pageNum) || pageNum < 1) {
      return;
    }
    if (this.num_of_pages > 0 && pageNum > this.num_of_pages) {
      return;
    }
    if (pageNum === this.currentPage) {
      return;
    }
    this.openHistory.clear();
    this.loadDashboard((pageNum - 1) * this.itemsPerPage);
  }

  submitGotoPage(): void {
    const total = Math.max(1, this.num_of_pages);
    const value = parseInt(this.gotoInput, 10);
    if (!value || value < 1 || value > total) {
      this.gotoError = true;
      setTimeout(() => {
        this.gotoError = false;
      }, 1800);
      return;
    }
    this.gotoInput = '';
    this.goToPage(value);
  }

  onGotoKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.submitGotoPage();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.num_of_pages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  // ---- status helpers -------------------------------------------------

  statusKey(doc: any): StatusKey {
    switch (Number(doc?.status)) {
      case 2: return 'processing';
      case 3: return 'failed';
      case 4: return 'draft';
      case 5: return 'published';
      default: return 'not_started';
    }
  }

  getStatusBadgeClass(doc: any): string {
    switch (this.statusKey(doc)) {
      case 'processing': return 'b-proc';
      case 'failed': return 'b-failed';
      case 'draft': return 'b-draft';
      case 'published': return 'b-pub';
      default: return 'b-none';
    }
  }

  getStatusText(doc: any): string {
    switch (this.statusKey(doc)) {
      case 'processing': return 'Processing';
      case 'failed': return 'Failed';
      case 'draft': return 'Draft';
      case 'published': return 'Published';
      default: return 'Not started';
    }
  }

  attemptBadgeClass(status: number): string {
    switch (Number(status)) {
      case 5:
      case 4: return 'ok';
      case 2: return 'run';
      default: return 'bad';
    }
  }

  attemptStatusText(status: number): string {
    switch (Number(status)) {
      case 5: return 'Published';
      case 4: return 'Completed';
      case 2: return 'Processing';
      case 3: return 'Failed';
      default: return 'Unknown';
    }
  }

  hasFile(doc: any): boolean {
    return !!(doc?.awsFilePath || doc?.localFilePath);
  }

  filePath(doc: any): string {
    return doc?.awsFilePath || doc?.localFilePath || '';
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  fileTypeLabel(doc: any): string {
    const raw = (doc?.fileType || '').toString().toLowerCase();
    if (raw.includes('pdf')) return 'PDF';
    if (raw.includes('xls')) return 'XLS';
    if (doc?.documentName) {
      const ext = doc.documentName.split('.').pop()?.toUpperCase();
      if (ext) return ext;
    }
    return 'FILE';
  }

  sampleSubLabel(doc: any): string {
    const type = this.fileTypeLabel(doc);
    const size = this.formatFileSize(doc?.fileSize || 0);
    return size ? `${type} · ${size}` : type;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // ---- attempt history ------------------------------------------------

  toggleHistory(doc: any): void {
    const id = String(doc.supplierId);
    if (this.openHistory.has(id)) {
      this.openHistory.delete(id);
      return;
    }
    this.openHistory.add(id);
    if (!this.attemptsMap[id]) {
      this.loadAttempts(id);
    }
  }

  isHistoryOpen(doc: any): boolean {
    return this.openHistory.has(String(doc.supplierId));
  }

  attemptsFor(doc: any): any[] {
    return this.attemptsMap[String(doc.supplierId)] || [];
  }

  isHistoryLoading(doc: any): boolean {
    return this.loadingHistory.has(String(doc.supplierId));
  }

  private loadAttempts(supplierId: string): void {
    this.loadingHistory.add(supplierId);
    this.documentMappingService.getSupplierMappingAttempts(supplierId).toPromise()
      .then((response: any) => {
        this.loadingHistory.delete(supplierId);
        this.attemptsMap[supplierId] = (response && response.data) ? response.data : [];
      })
      .catch(() => {
        this.loadingHistory.delete(supplierId);
        this.attemptsMap[supplierId] = [];
        this.dialog.error('Error', 'Failed to load attempt history. Please try again.');
      });
  }

  // ---- upload ---------------------------------------------------------

  private userId(): number {
    return this.basicService.user_data?.id || this.basicService.user_data?.userId || 0;
  }

  get canSubmitUpload(): boolean {
    return !!this.uploadFile && !!this.uploadSupplierId.trim() && !this.uploading;
  }

  triggerHeaderUpload(): void {
    this.openUploadModal();
  }

  uploadForSupplier(doc: any): void {
    if (!doc || !doc.supplierId) {
      this.dialog.error('Error', 'Supplier information is missing for this row.');
      return;
    }
    this.openUploadModal({
      supplierId: String(doc.supplierId),
      supplierName: doc.supplierName || '',
      locked: true
    });
  }

  private openUploadModal(opts?: { supplierId?: string; supplierName?: string; locked?: boolean }): void {
    this.uploadSupplierId = opts?.supplierId || '';
    this.uploadSupplierName = opts?.supplierName || '';
    this.uploadSupplierLocked = !!opts?.locked;
    this.uploadFile = null;
    this.uploadSuccess = false;
    this.uploadError = '';
    this.uploadProgress = 0;
    this.uploading = false;
    this.uploadDragging = false;
    this.uploadOpen = true;
  }

  browseFiles(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const input = this.fileInputRef?.nativeElement;
    if (input) {
      input.value = '';
      input.click();
    }
  }

  cancelUpload(): void {
    if (this.uploading) {
      return;
    }
    this.uploadOpen = false;
    this.uploadFile = null;
    this.uploadError = '';
    this.uploadSupplierId = '';
    this.uploadSupplierName = '';
    this.uploadSupplierLocked = false;
  }

  finishUpload(): void {
    this.uploadOpen = false;
    this.uploadFile = null;
    this.uploadSuccess = false;
    this.uploadSupplierId = '';
    this.uploadSupplierName = '';
    this.uploadSupplierLocked = false;
    this.loadDashboard(0);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.uploadDragging = true;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.uploadDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setUploadFile(file);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (file) {
      this.setUploadFile(file);
    }
  }

  setUploadFile(file: File): void {
    const name = file.name || '';
    const extension = name.split('.').pop()?.toLowerCase() || '';
    if (!['pdf', 'xls', 'xlsx', 'xlsm'].includes(extension)) {
      this.uploadError = 'PDF or Excel only. Please choose a .pdf, .xls, .xlsx, or .xlsm file.';
      this.uploadFile = null;
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.uploadError = 'Max file size is 10 MB. Please choose a smaller file.';
      this.uploadFile = null;
      return;
    }
    this.uploadError = '';
    this.uploadFile = file;
  }

  clearUploadFile(): void {
    this.uploadFile = null;
    const input = this.fileInputRef?.nativeElement;
    if (input) {
      input.value = '';
    }
  }

  submitUpload(): void {
    if (!this.canSubmitUpload || !this.uploadFile) {
      return;
    }
    const supplierId = this.uploadSupplierId.trim();
    if (!supplierId) {
      this.uploadError = 'Supplier ID is required.';
      return;
    }
    const userId = this.userId();
    if (!userId) {
      this.uploadError = 'User ID not found. Please login again.';
      return;
    }

    this.uploadError = '';
    this.uploading = true;
    this.uploadProgress = 0;
    if (this.uploadTimer) {
      clearInterval(this.uploadTimer);
    }
    this.uploadTimer = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 12;
      }
    }, 200);

    this.documentMappingService.uploadDocument(this.uploadFile, supplierId, userId).toPromise()
      .then((response: any) => {
        if (this.uploadTimer) {
          clearInterval(this.uploadTimer);
        }
        this.uploadProgress = 100;
        this.uploading = false;
        if (response && response.status === 'success') {
          this.uploadSuccess = true;
          this.uploadSuccessMessage =
            response.message ||
            `Sample invoice "${this.uploadFile?.name}" uploaded for supplier ${supplierId}. Extraction is in progress.`;
        } else {
          this.uploadError = (response && response.message) || 'Upload failed. Please try again.';
        }
      })
      .catch((error: any) => {
        if (this.uploadTimer) {
          clearInterval(this.uploadTimer);
        }
        this.uploading = false;
        this.uploadError = error?.error?.message || 'Upload failed. Please try again.';
      });
  }

  // ---- row actions ----------------------------------------------------

  openMapping(doc: any): void {
    if (!doc.documentId) {
      this.dialog.warning('No Document', 'There is no document to open for this supplier yet.');
      return;
    }
    this.router.navigate(['/admin/documentmapping/view', doc.documentId]);
  }

  downloadFile(doc: any): void {
    if (!doc?.documentId) {
      this.dialog.alert({
        title: 'Download',
        message: 'No sample file is available for this supplier.',
        type: 'info'
      });
      return;
    }
    this.downloadDocumentFile(doc.documentId, doc.documentName || `document-${doc.documentId}`);
  }

  private downloadDocumentFile(documentId: number, fileName?: string): void {
    this.basicService.showprogress = true;
    this.documentMappingService.getDocumentFileBlob(documentId).toPromise()
      .then((blob: any) => {
        this.basicService.showprogress = false;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || `document-${documentId}`;
        link.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      })
      .catch(() => {
        this.basicService.showprogress = false;
        this.dialog.error('Error', 'Failed to download the sample file. Please try again.');
      });
  }

  retryExtraction(doc: any): void {
    if (!doc.documentId) {
      this.dialog.warning('Cannot Retry', 'There is no document to retry for this supplier.');
      return;
    }
    this.dialog.confirm({
      title: 'Retry Extraction?',
      message: `Re-run extraction for the sample invoice of "${doc.supplierName || 'this supplier'}"?`,
      confirmText: 'Retry',
      cancelText: 'Cancel'
    }).then((ok) => {
      if (!ok) {
        return;
      }
      this.basicService.showprogress = true;
      this.documentMappingService.retryDocumentMapping(doc.documentId).toPromise()
        .then((response: any) => {
          this.basicService.showprogress = false;
          if (response && response.status === 'success') {
            this.dialog.success('Retry Started', response.message || 'Extraction has been re-queued.').then(() => {
              this.openHistory.clear();
              this.attemptsMap = {};
              this.loadDashboard((this.currentPage - 1) * this.itemsPerPage);
            });
          } else {
            this.dialog.error('Error', (response && response.message) || 'Failed to retry extraction.');
          }
        })
        .catch((error: any) => {
          this.basicService.showprogress = false;
          this.dialog.error('Error', error?.error?.message || 'Failed to retry extraction. Please try again.');
        });
    });
  }

  downloadAttempt(attempt: any): void {
    const documentId = attempt?.documentId || attempt?.id;
    if (!documentId) {
      this.dialog.alert({
        title: 'Download',
        message: 'File is not available for this attempt.',
        type: 'info'
      });
      return;
    }
    this.downloadDocumentFile(documentId, attempt?.documentName || `document-${documentId}`);
  }

  canDeleteAttempt(attempt: any): boolean {
    const id = attempt?.documentId || attempt?.id;
    const status = Number(attempt?.status);
    return !!id && (status === 3 || status === 4 || status === 5);
  }

  deleteMapping(doc: any): void {
    if (!doc?.documentId) {
      this.dialog.warning('Cannot Delete', 'There is no mapping document to delete for this supplier.');
      return;
    }
    const status = this.getStatusText(doc);
    const publishedNote =
      this.statusKey(doc) === 'published'
        ? ' This supplier will no longer have a published template for future invoices.'
        : '';
    this.dialog.confirm({
      title: 'Delete Mapping?',
      message: `Delete the ${status.toLowerCase()} sample "${doc.documentName || 'Untitled'}" for ${doc.supplierName || 'this supplier'}? This cannot be undone.${publishedNote}`,
      confirmText: 'Yes, delete it',
      cancelText: 'Cancel',
      danger: true
    }).then((ok) => {
      if (!ok) {
        return;
      }
      this.runDelete(doc.documentId, doc.supplierId, 'Mapping deleted successfully.');
    });
  }

  deleteAttempt(doc: any, attempt: any): void {
    const documentId = attempt?.documentId || attempt?.id;
    if (!documentId) {
      this.dialog.warning('Cannot Delete', 'This attempt has no associated document to delete.');
      return;
    }
    this.dialog.confirm({
      title: 'Delete Attempt?',
      message: `Delete the sample "${attempt.documentName || 'Untitled'}"? This action cannot be undone.`,
      confirmText: 'Yes, delete it',
      cancelText: 'Cancel',
      danger: true
    }).then((ok) => {
      if (!ok) {
        return;
      }
      this.runDelete(documentId, doc?.supplierId, 'Attempt deleted successfully.');
    });
  }

  private runDelete(documentId: number, supplierId: any, successMessage: string): void {
    this.basicService.showprogress = true;
    this.documentMappingService.deleteDocument(documentId).toPromise()
      .then((response: any) => {
        this.basicService.showprogress = false;
        if (response && response.status === 'success') {
          this.dialog.success('Deleted', successMessage).then(() => {
            const id = String(supplierId || '');
            if (id) {
              delete this.attemptsMap[id];
              if (this.openHistory.has(id)) {
                this.loadAttempts(id);
              }
            }
            this.loadDashboard((this.currentPage - 1) * this.itemsPerPage);
          });
        } else {
          this.dialog.error('Error', (response && response.message) || 'Failed to delete.');
        }
      })
      .catch((error: any) => {
        this.basicService.showprogress = false;
        this.dialog.error('Error', error?.error?.message || 'Failed to delete. Please try again.');
      });
  }

  // ---- data + polling -------------------------------------------------

  private hasProcessingRows(): boolean {
    return this.suppliers.some((doc) => Number(doc?.status) === 2);
  }

  private startPolling(): void {
    if (this.pollTimer) {
      return;
    }
    this.pollTimer = setInterval(() => {
      this.loadDashboard((this.currentPage - 1) * this.itemsPerPage, true);
    }, this.POLL_INTERVAL);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private syncPolling(): void {
    if (this.hasProcessingRows()) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  loadDashboard(offset: number = 0, silent: boolean = false): void {
    if (!silent) {
      this.basicService.showprogress = true;
    }
    const searchVal = (this.search_value && this.search_value.trim()) ? this.search_value.trim() : '';
    const status = this.currentStatusFilter();
    this.documentMappingService.getSupplierMappingDashboard(offset, searchVal, this.itemsPerPage, status).toPromise()
      .then((response: any) => {
        if (!silent) {
          this.basicService.showprogress = false;
        }
        if (response && response.status === 'success') {
          this.suppliers = response.data || [];
          this.totalrec = response.total_count != null ? response.total_count : 0;
          this.statusCounts = {
            all: 0, published: 0, draft: 0, failed: 0, processing: 0, not_started: 0,
            ...(response.status_counts || {})
          };
          this.currentPage = Math.floor(offset / this.itemsPerPage) + 1;
          this.index = this.currentPage;
          this.calculatePages();
          this.syncPolling();
        } else {
          if (!silent) {
            this.dialog.error('Error', (response && response.message) || 'Failed to load supplier dashboard.');
          }
          this.suppliers = [];
          this.totalrec = 0;
          this.calculatePages();
          this.stopPolling();
        }
      })
      .catch(() => {
        if (!silent) {
          this.basicService.showprogress = false;
          this.dialog.error('Error', 'Failed to load supplier dashboard. Please try again.');
          this.suppliers = [];
          this.totalrec = 0;
          this.calculatePages();
          this.stopPolling();
        }
      });
  }
}
